import { format, addDays } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { TrialStudent } from '~entities/student/api/get-all-trial-students';
import { useGetAvailableTrialSlots } from '~entities/student/api/get-available-trial-slots';
import { useRescheduleTrialBooking } from '~entities/student/api/reschedule-trial-booking';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { buildTrialRescheduleSlotLabel } from '~features/classes/ui/trial-reschedule-dialog.helpers';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

interface TrialRescheduleDialogProps {
  trial: TrialStudent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TrialRescheduleDialog({
  trial,
  open,
  onOpenChange,
}: TrialRescheduleDialogProps) {
  const classId = trial?.classId?._id ?? '';
  const startDate = format(new Date(), 'yyyy-MM-dd');
  const endDate = format(addDays(new Date(), 30), 'yyyy-MM-dd');
  const [selectedSlotKey, setSelectedSlotKey] = useState('');
  const studioTimezone = useCurrentStudio()?.timezone;

  const { data, isFetching } = useGetAvailableTrialSlots(
    open && classId
      ? {
          classId,
          startDate,
          endDate,
        }
      : null,
  );

  const slotTimezone = data?.timezone ?? studioTimezone;
  const mutation = useRescheduleTrialBooking();

  useEffect(() => {
    setSelectedSlotKey('');
  }, [open, trial?._id]);

  const slotOptions = useMemo(() => {
    return (data?.slots ?? []).map((slot) => ({
      key: `${slot.startTime}|${slot.endTime}`,
      label: buildTrialRescheduleSlotLabel(slot, slotTimezone),
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));
  }, [data?.slots, slotTimezone]);

  const handleReschedule = async () => {
    if (!trial) {
      return;
    }
    const slot = slotOptions.find((option) => option.key === selectedSlotKey);
    if (!slot) {
      toast.error('Please select a new time slot.');
      return;
    }

    try {
      await mutation.mutateAsync({
        trialId: trial._id,
        startTime: slot.startTime,
        endTime: slot.endTime,
      });
      toast.success('Trial rescheduled.');
      setSelectedSlotKey('');
      onOpenChange(false);
    } catch {
      toast.error('We could not reschedule this trial. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle>Reschedule trial</DialogTitle>
          <DialogDescription>
            Choose a new available time for {trial?.studentName ?? 'this student'}.
          </DialogDescription>
        </DialogHeader>

        {isFetching ? (
          <div className='flex items-center justify-center py-8 text-muted-foreground'>
            <Loader2 className='mr-2 size-4 animate-spin' />
            Loading available slots...
          </div>
        ) : slotOptions.length === 0 ? (
          <p className='py-4 text-sm text-muted-foreground'>
            No open trial slots were found in the next 30 days.
          </p>
        ) : (
          <Select value={selectedSlotKey} onValueChange={setSelectedSlotKey}>
            <SelectTrigger>
              <SelectValue placeholder='Select a new slot' />
            </SelectTrigger>
            <SelectContent>
              {slotOptions.map((slot) => (
                <SelectItem key={slot.key} value={slot.key}>
                  {slot.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={() => void handleReschedule()}
            disabled={mutation.isPending || !selectedSlotKey}
          >
            {mutation.isPending ? (
              <Loader2 className='size-4 animate-spin' />
            ) : (
              'Reschedule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
